<template>
  <div class="configuration-wrapper">
    <Panel>
      <template #header>
        <div class="panel-header-content">
          <h4>{{ t('templateBuilder.panels.cssPanel.header') }}</h4>
        </div>
      </template>

      <template
        v-if="!widgetId"
        #icons
      >
        <i
          class="far fa-times icons-header-panel"
          @click="hideLeftToolbar"
        />
      </template>

      <div class="field-group pt-3">
        <div>
          <div class="field-group-content">
            <div class="field-wrapper">
              <div class="p-fluid flex">
                <BaseInformation
                  :message="message"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="field-group pt-3">
        <div>
          <div class="field-group-content">
            <div class="field-wrapper">
              <div class="p-fluid flex">
                <div class="codemirror-wrapper">
                  <div class="codemirror-header">
                    <i
                      class="fas fa-expand-alt expand-code-btn"
                      :title="t('templateBuilder.codeEditor.expandToModal')"
                      @click="openCodeModal"
                    />
                  </div>
                  <VCodeMirror
                    ref="codeMirrorEditorRef"
                    v-model:value="inputValue"
                    :options="cmOptions"
                    :auto-height="true"
                    @keyup="handleValueChange"
                    @paste="handleValueChange"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Panel>

    <!-- Modal CodeMirror -->
    <CodeMirrorModal
      v-model:visible="showCodeModal"
      v-model:code="inputValue"
      :language="'css'"
      :title="t('templateBuilder.panels.cssPanel.modalTitle')"
      @apply="handleModalApply"
    />
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  onMounted,
  Ref,
  ref,
  computed,
} from 'vue';
import BaseInformation from '@/components/fields/BaseInformation.vue';
import { VCodeMirror } from 'vue3-code-mirror';
import { isDisplayTemplate } from '@/components/template-builder/utils/helpers';
import { hideLeftToolbar, TemplateEditorState, setChangeInLeftToolbar } from '@/composables/template-editor/TemplateEditor';
import {
  getCssStyles,
  setCssStyles,
} from '@/components/template-builder/setters';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/store';
import Panel from 'primevue/panel';
import CodeMirrorModal from '@/components/common/CodeMirrorModal.vue';
import {
  CUSTOM_WIDGET_CSS_PREFIX,
  TEMPLATE_SYNC_ELEMENT_IDENTIFIER,
  TEMPLATE_SYNC_ELEMENT_NAME,
  TEMPLATE_WIDGET_IDENTIFIER_PREFIX,
} from '@/components/template-builder/utils/constants';
import { TemplateStructureEnum } from '@/types';

export default defineComponent({
  name: 'CssPanel',

  components: {
    BaseInformation,
    VCodeMirror,
    Panel,
    CodeMirrorModal,
  },

  props: {
    widgetId: {
      type: String,
      required: false,
      default: '',
    },
  },

  setup(props) {
    const { t } = useI18n();
    const store = useStore();

    const cmOptions = {
      mode: 'css', // Language mode
      theme: 'default', // Theme
      lineNumbers: true, // Show line number
      smartIndent: true, // Smart indent
      indentUnit: 4, // The smart indent unit is 2 spaces in length
      foldGutter: true, // Code folding
      styleActiveLine: true, // Display the style of the selected row
    };

    const inputValue: Ref<string> = ref('');
    const codeMirrorEditorRef = ref();
    const message: Ref<string> = ref('');
    const cssType = ref('static');
    const isDesignSync = ref(false);
    const headerTitle = ref('');
    const backupValue = ref('');
    const selectedSyncElement = computed(() => store.getters['liveEditor/getSelectedSyncElement']);
    const showCodeModal = ref(false);

    // Timeout pour debounce
    let handleValueChangeTimeout: NodeJS.Timeout | null = null;

    const handleValueChange = () => {
      // Annuler le timeout précédent s'il existe
      if (handleValueChangeTimeout) {
        clearTimeout(handleValueChangeTimeout);
      }

      // Créer un nouveau timeout
      handleValueChangeTimeout = setTimeout(() => {
        // Insert new value in iframe (optional : create the style tag if doesn't exist)
        const acceptCallback = (addHistory = true) => {
          let sectionId = null;
          if (isDisplayTemplate(TemplateEditorState.template?.type)) {
            sectionId = TemplateEditorState.template?.sections[0].id_template_elements.toString();
          } else {
            sectionId = TemplateEditorState.template?.content.design.toString();
          }
          setCssStyles(cssType.value, inputValue.value, sectionId);
          if (addHistory) {
            setChangeInLeftToolbar();
          }
        };

        if (isDesignSync.value && !selectedSyncElement.value) {
          store.dispatch('liveEditor/showSyncElementWarning', {
            elementId: 'spm_body',
            elementType: TemplateStructureEnum.DESIGN,
            name: headerTitle.value,
            acceptCallback: () => acceptCallback(false),
            rejectCallback: () => {
              inputValue.value = backupValue.value;
            },
          });
        } else {
          acceptCallback(selectedSyncElement.value === null);
        }
      }, 500); // Délai de 300ms
    };

    onMounted(() => {
      message.value = isDisplayTemplate(TemplateEditorState.template?.type) ? 'templateBuilder.informations.cssPanelDisplay' : 'templateBuilder.informations.cssPanelEmail';
      if (TemplateEditorState.template && TemplateEditorState.template.informations.imported) {
        cssType.value = 'dynamic';
      }
      if (props.widgetId) {
        cssType.value = `${CUSTOM_WIDGET_CSS_PREFIX}${props.widgetId.split(TEMPLATE_WIDGET_IDENTIFIER_PREFIX)[1]}`;
      }
      inputValue.value = getCssStyles(cssType.value, true);
      if (TemplateEditorState.template) {
        let design = null;
        if (isDisplayTemplate(TemplateEditorState.template?.type)) {
          design = TemplateEditorState.template?.sections[0];
        } else {
          design = TemplateEditorState.template.sections.find((section) => section.type === TemplateStructureEnum.DESIGN);
        }
        if (design) {
          const data = JSON.parse(design.data || '{}');
          if (data[TEMPLATE_SYNC_ELEMENT_IDENTIFIER] && data[TEMPLATE_SYNC_ELEMENT_NAME]) {
            isDesignSync.value = true;
            backupValue.value = inputValue.value;
            headerTitle.value = data[TEMPLATE_SYNC_ELEMENT_NAME];
          }
        }
      }
    });

    const openCodeModal = () => {
      showCodeModal.value = true;
    };

    const handleModalApply = (code: string) => {
      inputValue.value = code;
      handleValueChange();
    };

    return {
      t,
      cmOptions,
      inputValue,
      codeMirrorEditorRef,
      handleValueChange,
      message,
      hideLeftToolbar,
      showCodeModal,
      openCodeModal,
      handleModalApply,
    };
  },
});
</script>

<style lang="scss" scoped>
.v-code-mirror {
  width: 100%;
}

:deep() .CodeMirror {
  height: 100% !important;
}

.icons-header-panel {
  cursor: pointer;
  color: #607D8B;
}

.field-group-content {
  .field-wrapper:not(:last-child) {
    margin-bottom: 1.5rem;
  }
}

/* Styles pour le wrapper CodeMirror avec bouton d'élargissement */
.codemirror-wrapper {
  position: relative;
  width: 100%;
}

.codemirror-header {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  
  .expand-code-btn {
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid #ddd;
    border-radius: 4px;
    padding: 6px 8px;
    cursor: pointer;
    color: #607D8B;
    font-size: 12px;
    transition: all 0.2s ease;
    
    &:hover {
      background: #f8f9fa;
      color: #495057;
      transform: scale(1.05);
    }
  }
}
</style>
